import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useAdminPermissions, type AdminPermission } from '@/hooks/useAdminPermissions';
import { Skeleton } from '@/components/ui/skeleton';
import { Card } from '@/components/ui/card';
import { ShieldX } from 'lucide-react';

interface AdminRouteProps {
  children: React.ReactNode;
  requiredPermission?: AdminPermission;
}

export function AdminRoute({ children, requiredPermission }: AdminRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const { role, hasPermission, isLoading: permLoading } = useAdminPermissions();

  if (authLoading || permLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="space-y-4 w-full max-w-md px-6">
          <Skeleton className="h-12 w-3/4 mx-auto" />
          <Skeleton className="h-8 w-1/2 mx-auto" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Must have admin or moderator role
  if (!role) {
    return <Navigate to="/" replace />;
  }

  // Check specific permission if required
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
        <Card className="max-w-md text-center p-6">
          <ShieldX className="w-12 h-12 text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Không có quyền truy cập</h2>
          <p className="text-muted-foreground">
            Tài khoản {role === 'moderator' ? 'Moderator' : 'Admin'} của bạn không có quyền truy cập module này.
          </p>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
